import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";
import { BACKEND_URL } from "../lib/backend";

const heading = { fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" };

const PACKAGE_LABELS = {
  quick_diagnosis: "Quick Diagnosis (€99)",
  deep_audit: "Deep Audit (€249)",
  fix_sprint: "Fix Sprint (from €499 — request)",
};

const TURNAROUNDS = {
  quick_diagnosis: "24–48 hours after project access is received.",
  deep_audit: "Approximately 48 hours after project access is received.",
};

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm";

const Label = ({ children, required }) => (
  <label className="block text-sm font-semibold text-slate-200 mb-2">
    {children} {required && <span className="text-teal-300">*</span>}
  </label>
);

const IntakeHeader = () => (
  <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/developer-audit" className="flex items-center gap-3" data-testid="intake-logo-link">
        <img src="/nephrix-logo.png" alt="Nephrix" className="h-10 w-auto" />
        <span className="text-slate-100 font-semibold text-lg tracking-tight hidden sm:flex items-baseline gap-2" style={heading}>
          Nephrix
          <span className="text-teal-300 text-xs font-bold tracking-widest uppercase">Developer</span>
        </span>
      </Link>
      <Link
        to="/developer-audit"
        className="flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors"
        data-testid="intake-back-link"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Developer Audit
      </Link>
    </div>
  </header>
);

export default function DeveloperAuditIntake() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paramPackage = searchParams.get("package");

  // payment verification: idle | verifying | paid | failed
  const [payState, setPayState] = useState(sessionId ? "verifying" : "idle");
  const [pkg, setPkg] = useState(paramPackage === "fix-sprint" ? "fix_sprint" : "");

  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    company_or_project: "",
    stack: "",
    project_size: "",
    build_method: "",
    repository_url: "",
    main_problem: "",
    additional_notes: "",
    authorized: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const verifyPayment = useCallback(async () => {
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") {
            setPayState("paid");
            if (data.package) setPkg(data.package);
            return;
          }
          if (["expired", "failed"].includes(data.payment_status)) {
            setPayState("failed");
            return;
          }
        }
      } catch (_e) {
        /* retry */
      }
      if (attempts < 15) setTimeout(poll, 2000);
      else setPayState("failed");
    };
    poll();
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) verifyPayment();
  }, [sessionId, verifyPayment]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.authorized) {
      setError("Please confirm you are authorized to provide access to this project's codebase.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          package: pkg,
          session_id: sessionId || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDone(data);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const detail = data?.detail;
        setError(typeof detail === "string" ? detail : "Please check the form fields and try again.");
      }
    } catch (_e) {
      setError("Connection error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isPaidFlow = payState === "paid";
  const showForm = payState === "paid" || payState === "idle";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 antialiased" data-testid="audit-intake-page">
      <IntakeHeader />
      <main className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-teal-500/10 blur-[120px]" />
        <div className="relative max-w-2xl mx-auto px-6 py-14 md:py-20">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="intake-success">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-teal-500/10 border border-teal-400/25 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-teal-300" />
              </div>
              <h1 className="mt-6 text-3xl md:text-4xl font-bold text-slate-50" style={heading}>
                Your audit is reserved.
              </h1>
              <p className="mt-4 text-slate-300 leading-relaxed">
                We've received your project information.
                <br />
                We'll review the details and contact you regarding secure project access.
              </p>
              {TURNAROUNDS[pkg] && (
                <p className="mt-6 inline-flex px-5 py-3 rounded-xl bg-teal-500/10 border border-teal-400/20 text-teal-300 text-sm font-semibold" data-testid="intake-turnaround">
                  Expected turnaround: {TURNAROUNDS[pkg]}
                </p>
              )}
              <div className="mt-10">
                <Link to="/developer-audit" className="text-sm text-slate-400 hover:text-teal-300 transition-colors">
                  ← Back to Nephrix Developer Audit
                </Link>
              </div>
            </motion.div>
          ) : (
            <>
              {payState === "verifying" && (
                <div className="text-center py-24" data-testid="payment-verifying">
                  <Loader2 className="w-8 h-8 text-teal-300 animate-spin mx-auto" />
                  <p className="mt-4 text-slate-300">Verifying your payment…</p>
                </div>
              )}

              {payState === "failed" && (
                <div className="text-center py-24" data-testid="payment-failed">
                  <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
                  <p className="mt-4 text-slate-200 font-semibold">We could not confirm this payment.</p>
                  <p className="mt-2 text-sm text-slate-400">
                    If you were charged, contact us at beta@nephrixapp.com and we will sort it out immediately.
                  </p>
                  <Link
                    to="/developer-audit"
                    className="mt-8 inline-flex px-6 py-3 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-sm transition-colors"
                  >
                    Back to pricing
                  </Link>
                </div>
              )}

              {showForm && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  {isPaidFlow ? (
                    <div className="text-center" data-testid="payment-received-banner">
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-400/25 text-teal-300 text-xs font-semibold tracking-widest uppercase">
                        <CheckCircle2 className="w-4 h-4" />
                        Payment received.
                      </span>
                      <h1 className="mt-5 text-3xl md:text-4xl font-bold text-slate-50" style={heading}>
                        Let's prepare your Nephrix Developer Audit.
                      </h1>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h1 className="text-3xl md:text-4xl font-bold text-slate-50" style={heading} data-testid="intake-title">
                        {pkg === "fix_sprint" ? "Request your Fix Sprint." : "Project intake."}
                      </h1>
                      <p className="mt-3 text-slate-300 text-sm md:text-base">
                        {pkg === "fix_sprint"
                          ? "Tell us about your project — we'll review it and get back to you with scope and pricing."
                          : "Tell us about your project so we can prepare your audit."}
                      </p>
                    </div>
                  )}

                  <form onSubmit={submit} className="mt-10 space-y-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-6 md:p-8" data-testid="intake-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label required>Full name</Label>
                        <input className={inputCls} required maxLength={120} value={form.customer_name} onChange={set("customer_name")} data-testid="intake-name" />
                      </div>
                      <div>
                        <Label required>Email</Label>
                        <input type="email" className={inputCls} required maxLength={200} value={form.email} onChange={set("email")} data-testid="intake-email" />
                      </div>
                    </div>

                    <div>
                      <Label>Company / Project name</Label>
                      <input className={inputCls} maxLength={160} value={form.company_or_project} onChange={set("company_or_project")} data-testid="intake-company" />
                    </div>

                    <div>
                      <Label required>Purchased package / requested service</Label>
                      {pkg ? (
                        <div className="px-4 py-3 rounded-xl bg-teal-500/10 border border-teal-400/25 text-teal-300 text-sm font-semibold" data-testid="intake-package-display">
                          {PACKAGE_LABELS[pkg] || pkg}
                        </div>
                      ) : (
                        <>
                          <select className={inputCls} required value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="intake-package-select">
                            <option value="">Select a service…</option>
                            <option value="fix_sprint">{PACKAGE_LABELS.fix_sprint}</option>
                          </select>
                          <p className="mt-2 text-xs text-slate-400">
                            For the paid Quick Diagnosis or Deep Audit, please{" "}
                            <Link to="/developer-audit" className="text-teal-300 hover:underline">
                              purchase from the pricing page
                            </Link>{" "}
                            first.
                          </p>
                        </>
                      )}
                    </div>

                    <div>
                      <Label required>Primary technology / stack</Label>
                      <input className={inputCls} required maxLength={200} placeholder="e.g. React + FastAPI + MongoDB" value={form.stack} onChange={set("stack")} data-testid="intake-stack" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label required>Project size</Label>
                        <select className={inputCls} required value={form.project_size} onChange={set("project_size")} data-testid="intake-size">
                          <option value="">Select…</option>
                          <option value="small_mvp">Small MVP</option>
                          <option value="medium">Medium application</option>
                          <option value="large">Large application</option>
                          <option value="not_sure">Not sure</option>
                        </select>
                      </div>
                      <div>
                        <Label required>How was the project built?</Label>
                        <select className={inputCls} required value={form.build_method} onChange={set("build_method")} data-testid="intake-build-method">
                          <option value="">Select…</option>
                          <option value="traditional">Traditional development</option>
                          <option value="cursor">Cursor</option>
                          <option value="claude">Claude / Claude Code</option>
                          <option value="lovable">Lovable</option>
                          <option value="bolt">Bolt</option>
                          <option value="replit">Replit</option>
                          <option value="other_ai">Other AI tools</option>
                          <option value="mixed">Mixed</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label>Repository URL</Label>
                      <input className={inputCls} maxLength={300} placeholder="https://github.com/you/project (optional)" value={form.repository_url} onChange={set("repository_url")} data-testid="intake-repo" />
                      <p className="mt-2 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                        <ShieldCheck className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                        Optional at this stage. Never share passwords or access tokens here. After reviewing your intake,
                        we will contact you with the appropriate secure method for providing repository or project access.
                      </p>
                    </div>

                    <div>
                      <Label required>What is currently giving you the most trouble?</Label>
                      <textarea className={`${inputCls} min-h-[140px] resize-y`} required maxLength={5000} value={form.main_problem} onChange={set("main_problem")} data-testid="intake-problem" />
                    </div>

                    <div>
                      <Label>Additional notes</Label>
                      <textarea className={`${inputCls} min-h-[90px] resize-y`} maxLength={5000} value={form.additional_notes} onChange={set("additional_notes")} data-testid="intake-notes" />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.authorized}
                        onChange={set("authorized")}
                        className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-900 text-teal-500 focus:ring-teal-500"
                        data-testid="intake-authorized"
                      />
                      <span className="text-sm text-slate-300 leading-relaxed">
                        I confirm that I am authorized to provide access to this project's codebase. <span className="text-teal-300">*</span>
                      </span>
                    </label>

                    {error && (
                      <p className="text-sm text-red-400" data-testid="intake-error">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !pkg}
                      className="w-full py-4 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold transition-all shadow-[0_10px_40px_-10px_rgba(20,184,166,0.6)] disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="intake-submit"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? "Submitting…" : pkg === "fix_sprint" ? "Request Fix Sprint" : "Reserve My Audit"}
                    </button>
                  </form>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
